import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../lib/api";

/**
 * SiteContent — single fetch on app mount, exposes editable copy (Hero,
 * Packages, Services taglines) + social links to the entire tree. Admin
 * changes propagate after refresh; no realtime needed for marketing copy.
 *
 * Components consume:
 *   const { text, links } = useSiteContent();
 *   text.hero.headline_line1 ...
 *   links.facebook ...
 *
 * Falls back to empty strings while loading so JSX never renders `undefined`.
 */
const SiteContentContext = createContext({
  text: {
    hero: {}, packages: {}, services: {},
    business_hours: {}, faq_list: [], about_section: { boxes: [] },
    how_it_works: { steps: [] }, announcement_banner: { active: false, text: "" },
    google_reviews: { rating: "", review_count: "", reviews: [] },
  },
  links: { facebook: "", instagram: "", youtube: "", googleReviews: "", tiktok: "" },
  loading: true,
  refresh: () => {},
});

const EMPTY_TEXT = {
  hero: {}, packages: {}, services: {},
  business_hours: {}, faq_list: [], about_section: { boxes: [] },
  how_it_works: { steps: [] }, announcement_banner: { active: false, text: "" },
  google_reviews: { rating: "", review_count: "", reviews: [] },
};
const EMPTY_LINKS = { facebook: "", instagram: "", youtube: "", googleReviews: "", tiktok: "" };

export function SiteContentProvider({ children }) {
  const [text, setText] = useState(EMPTY_TEXT);
  const [links, setLinks] = useState(EMPTY_LINKS);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const [textRes, linksRes] = await Promise.all([
        api.get("/site-text/public"),
        api.get("/social-links/public"),
      ]);
      setText(textRes.data?.sections || EMPTY_TEXT);
      setLinks(linksRes.data?.links || EMPTY_LINKS);
    } catch (e) {
      // Network or backend down — keep defaults
      console.warn("[SiteContent] fetch failed", e?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <SiteContentContext.Provider value={{ text, links, loading, refresh }}>
      {children}
    </SiteContentContext.Provider>
  );
}

export const useSiteContent = () => useContext(SiteContentContext);
